'use client';
import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Search, Filter, Download, RefreshCw, ChevronUp, ChevronDown,
  ChevronsUpDown, Eye, AlertTriangle, CheckCircle2, Clock,
  FileEdit, Trash2, Ship, X, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { MOCK_INSPECTIONS, InspectionRecord } from './mockInspections';
import InspectionDetailDrawer from './InspectionDetailDrawer';

type SortKey = keyof InspectionRecord;
type SortDir = 'asc' | 'desc';

const STATUS_CONFIG: Record<string, { label: string; class: string }> = {
  Submitted: { label: 'Submitted', class: 'badge bg-blue-50 text-blue-700 border border-blue-200' },
  'Under Review': { label: 'Under Review', class: 'badge bg-amber-50 text-amber-700 border border-amber-200' },
  Closed: { label: 'Closed', class: 'badge bg-green-50 text-green-700 border border-green-200' },
  Draft: { label: 'Draft', class: 'badge bg-gray-100 text-gray-600 border border-gray-200' },
};

const SCOPE_COLORS: Record<string, string> = {
  'Anglo American': 'bg-blue-50 text-blue-700',
  FMG: 'bg-purple-50 text-purple-700',
  'Rio Tinto': 'bg-red-50 text-red-700',
  Vale: 'bg-green-50 text-green-700',
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];

export default function DataBoardClient() {
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    inspectionType: '',
    customerScope: '',
    owner: '',
    manager: '',
    inspector: '',
    pscDetention: '',
    dateFrom: '',
    dateTo: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('dateOfInspection');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [drawerRecord, setDrawerRecord] = useState<InspectionRecord | null>(null);

  const uniqueOwners = Array.from(new Set(MOCK_INSPECTIONS.map((i) => i.owner)));
  const uniqueManagers = Array.from(new Set(MOCK_INSPECTIONS.map((i) => i.manager)));
  const uniqueInspectors = Array.from(new Set(MOCK_INSPECTIONS.map((i) => i.inspector)));
  const uniqueScopes = Array.from(new Set(MOCK_INSPECTIONS.map((i) => i.customerScope)));

  const filtered = useMemo(() => {
    let data = [...MOCK_INSPECTIONS];
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(
        (r) =>
          r.vesselName.toLowerCase().includes(q) ||
          r.imoNumber.includes(q) ||
          r.inspector.toLowerCase().includes(q) ||
          r.port.toLowerCase().includes(q)
      );
    }
    if (filters.status) data = data.filter((r) => r.status === filters.status);
    if (filters.inspectionType) data = data.filter((r) => r.inspectionType === filters.inspectionType);
    if (filters.customerScope) data = data.filter((r) => r.customerScope === filters.customerScope);
    if (filters.owner) data = data.filter((r) => r.owner === filters.owner);
    if (filters.manager) data = data.filter((r) => r.manager === filters.manager);
    if (filters.inspector) data = data.filter((r) => r.inspector === filters.inspector);
    if (filters.pscDetention === 'yes') data = data.filter((r) => r.pscDetention);
    if (filters.pscDetention === 'no') data = data.filter((r) => !r.pscDetention);
    if (filters.dateFrom) data = data.filter((r) => r.dateOfInspection >= filters.dateFrom);
    if (filters.dateTo) data = data.filter((r) => r.dateOfInspection <= filters.dateTo);

    data.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return data;
  }, [search, filters, sortKey, sortDir]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return <ChevronsUpDown size={12} className="text-muted ml-1 inline" />;
    return sortDir === 'asc'
      ? <ChevronUp size={12} className="ml-1 inline" style={{ color: 'hsl(var(--primary))' }} />
      : <ChevronDown size={12} className="ml-1 inline" style={{ color: 'hsl(var(--primary))' }} />;
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === paginated.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(paginated.map((r) => r.id)));
  };

  const clearFilters = () => {
    setFilters({ status: '', inspectionType: '', customerScope: '', owner: '', manager: '', inspector: '', pscDetention: '', dateFrom: '', dateTo: '' });
    setSearch('');
    setPage(1);
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length + (search ? 1 : 0);

  // Summary stats
  const summaryStats = useMemo(() => ({
    total: filtered.length,
    critical: filtered.filter((r) => r.criticalFindings > 0).length,
    detained: filtered.filter((r) => r.pscDetention).length,
    avgLsd: filtered.length ? Math.round(filtered.reduce((s, r) => s + r.totalLsdScore, 0) / filtered.length) : 0,
  }), [filtered]);

  const handleExport = () => {
    // TODO: Backend integration — POST /api/inspections/export with selected IDs
    toast.success(`Exporting ${selectedIds.size > 0 ? selectedIds.size : filtered.length} inspections to Excel`);
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Page Header */}
      <div className="px-6 py-5 border-b bg-white sticky top-0 z-20" style={{ borderColor: 'hsl(var(--border))' }}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold">Inspection Data Board</h1>
            <p className="text-xs text-muted mt-0.5">Miner Inspection Checklist — Central Repository</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              className="btn-secondary text-xs"
              onClick={() => { toast.info('Refreshing data...'); }}
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button className="btn-secondary text-xs" onClick={handleExport}>
              <Download size={14} /> Export
              {selectedIds.size > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold"
                  style={{ background: 'hsl(var(--primary))', color: '#fff' }}>
                  {selectedIds.size}
                </span>
              )}
            </button>
            <a href="/inspection-application" className="btn-primary text-xs">
              + New Inspection
            </a>
          </div>
        </div>
      </div>

      <div className="flex-1 px-6 py-5 space-y-4 max-w-screen-2xl mx-auto w-full">
        {/* Summary Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total Inspections', value: summaryStats.total, color: 'hsl(var(--primary))' },
            { label: 'With Critical Findings', value: summaryStats.critical, color: 'hsl(var(--destructive))' },
            { label: 'PSC Detentions', value: summaryStats.detained, color: 'hsl(38 92% 50%)' },
            { label: 'Avg LSD Score', value: summaryStats.avgLsd, color: 'hsl(var(--foreground))' },
          ].map(({ label, value, color }) => (
            <div key={`stat-${label}`} className="bg-white rounded-xl border p-4 shadow-card" style={{ borderColor: 'hsl(var(--border))' }}>
              <p className="text-xs text-muted mb-1">{label}</p>
              <p className="text-2xl font-bold tabular-nums" style={{ color }}>{value}</p>
            </div>
          ))}
        </div>

        {/* Search + Filter Bar */}
        <div className="bg-white rounded-xl border shadow-card p-4" style={{ borderColor: 'hsl(var(--border))' }}>
          <div className="flex gap-3 flex-wrap items-center">
            <div className="relative flex-1 min-w-48">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                className="input-field pl-8 text-sm"
                placeholder="Search vessel name, IMO, inspector, port..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            <button
              className={`btn-secondary text-xs flex items-center gap-2 ${activeFilterCount > 0 ? 'border-primary text-primary' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={14} />
              Filters
              {activeFilterCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-xs font-bold"
                  style={{ background: 'hsl(var(--primary))', color: '#fff' }}>
                  {activeFilterCount}
                </span>
              )}
            </button>
            {activeFilterCount > 0 && (
              <button className="text-xs text-muted hover:text-destructive flex items-center gap-1 transition-colors" onClick={clearFilters}>
                <X size={12} /> Clear all
              </button>
            )}
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 animate-fade-in"
              style={{ borderColor: 'hsl(var(--border))' }}>
              <div>
                <label className="block text-xs font-semibold mb-1 text-muted">Status</label>
                <select className="input-field text-xs" value={filters.status}
                  onChange={(e) => { setFilters((f) => ({ ...f, status: e.target.value })); setPage(1); }}>
                  <option value="">All statuses</option>
                  {['Draft', 'Submitted', 'Under Review', 'Closed'].map((s) => (
                    <option key={`fstatus-${s}`} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-muted">Inspection Type</label>
                <select className="input-field text-xs" value={filters.inspectionType}
                  onChange={(e) => { setFilters((f) => ({ ...f, inspectionType: e.target.value })); setPage(1); }}>
                  <option value="">Full & Partial</option>
                  <option value="Full">Full</option>
                  <option value="Partial">Partial</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-muted">Customer Scope</label>
                <select className="input-field text-xs" value={filters.customerScope}
                  onChange={(e) => { setFilters((f) => ({ ...f, customerScope: e.target.value })); setPage(1); }}>
                  <option value="">All scopes</option>
                  {uniqueScopes.map((s) => <option key={`fscope-${s}`} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-muted">Ship Owner</label>
                <select className="input-field text-xs" value={filters.owner}
                  onChange={(e) => { setFilters((f) => ({ ...f, owner: e.target.value })); setPage(1); }}>
                  <option value="">All owners</option>
                  {uniqueOwners.map((o) => <option key={`fowner-${o}`} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-muted">Ship Manager</label>
                <select className="input-field text-xs" value={filters.manager}
                  onChange={(e) => { setFilters((f) => ({ ...f, manager: e.target.value })); setPage(1); }}>
                  <option value="">All managers</option>
                  {uniqueManagers.map((m) => <option key={`fmgr-${m}`} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-muted">Inspector</label>
                <select className="input-field text-xs" value={filters.inspector}
                  onChange={(e) => { setFilters((f) => ({ ...f, inspector: e.target.value })); setPage(1); }}>
                  <option value="">All inspectors</option>
                  {uniqueInspectors.map((i) => <option key={`finsp-${i}`} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-muted">PSC Detention</label>
                <select className="input-field text-xs" value={filters.pscDetention}
                  onChange={(e) => { setFilters((f) => ({ ...f, pscDetention: e.target.value })); setPage(1); }}>
                  <option value="">Any</option>
                  <option value="yes">Detained</option>
                  <option value="no">Not Detained</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-muted">Date From</label>
                <input type="date" className="input-field text-xs" value={filters.dateFrom}
                  onChange={(e) => { setFilters((f) => ({ ...f, dateFrom: e.target.value })); setPage(1); }} />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-muted">Date To</label>
                <input type="date" className="input-field text-xs" value={filters.dateTo}
                  onChange={(e) => { setFilters((f) => ({ ...f, dateTo: e.target.value })); setPage(1); }} />
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border shadow-card overflow-hidden" style={{ borderColor: 'hsl(var(--border))' }}>
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full min-w-[1200px]">
              <thead>
                <tr className="border-b" style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--secondary))' }}>
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === paginated.length && paginated.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded"
                    />
                  </th>
                  {[
                    { key: 'vesselName' as SortKey, label: 'Vessel' },
                    { key: 'imoNumber' as SortKey, label: 'IMO' },
                    { key: 'inspectionType' as SortKey, label: 'Type' },
                    { key: 'dateOfInspection' as SortKey, label: 'Date' },
                    { key: 'inspector' as SortKey, label: 'Inspector' },
                    { key: 'customerScope' as SortKey, label: 'Scope' },
                    { key: 'totalLsdScore' as SortKey, label: 'LSD Score' },
                    { key: 'criticalFindings' as SortKey, label: 'Critical' },
                    { key: 'pscDeficiencies' as SortKey, label: 'PSC Def.' },
                    { key: 'status' as SortKey, label: 'Status' },
                  ].map(({ key, label }) => (
                    <th
                      key={`th-${key}`}
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer select-none whitespace-nowrap"
                      style={{ color: 'hsl(var(--muted-foreground))' }}
                      onClick={() => handleSort(key)}
                    >
                      {label}<SortIcon k={key} />
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <Ship size={32} className="text-muted" />
                        <p className="font-semibold text-sm">No inspections found</p>
                        <p className="text-xs text-muted">Try adjusting your search or filter criteria</p>
                        <button className="btn-secondary text-xs" onClick={clearFilters}>Clear filters</button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginated.map((record, idx) => (
                    <tr
                      key={`row-${record.id}`}
                      className={`table-row-hover border-b last:border-b-0 ${idx % 2 === 1 ? '' : ''} ${selectedIds.has(record.id) ? 'bg-blue-50/40' : ''}`}
                      style={{ borderColor: 'hsl(var(--border))' }}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(record.id)}
                          onChange={() => toggleSelect(record.id)}
                          className="rounded"
                        />
                      </td>
                      {/* Vessel */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {record.pscDetention && (
                            <AlertTriangle size={13} style={{ color: 'hsl(var(--destructive))', flexShrink: 0 }} title="PSC Detention" />
                          )}
                          <div>
                            <p className="text-sm font-semibold whitespace-nowrap">{record.vesselName}</p>
                            <p className="text-xs text-muted">{record.vesselType} · {record.flag}</p>
                          </div>
                        </div>
                      </td>
                      {/* IMO */}
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs font-semibold">{record.imoNumber}</span>
                      </td>
                      {/* Type */}
                      <td className="px-4 py-3">
                        <span className={`badge text-xs ${record.inspectionType === 'Full' ? 'badge-normal' : 'badge-moderate'}`}>
                          {record.inspectionType}
                        </span>
                      </td>
                      {/* Date */}
                      <td className="px-4 py-3">
                        <p className="text-xs font-medium whitespace-nowrap">{record.dateOfInspection}</p>
                        <p className="text-xs text-muted">{record.port}, {record.country}</p>
                      </td>
                      {/* Inspector */}
                      <td className="px-4 py-3">
                        <p className="text-xs font-medium whitespace-nowrap">{record.inspector}</p>
                        <p className="text-xs text-muted truncate max-w-[140px]">{record.inspectorOrg}</p>
                      </td>
                      {/* Scope */}
                      <td className="px-4 py-3">
                        <span className={`badge text-xs ${SCOPE_COLORS[record.customerScope] || 'bg-gray-100 text-gray-600'}`}>
                          {record.customerScope}
                        </span>
                      </td>
                      {/* LSD Score */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span
                            className="font-mono text-sm font-bold tabular-nums px-2 py-1 rounded-lg"
                            style={{
                              background: record.totalLsdScore > 60 ? 'hsl(355 75% 58% / 0.12)' :
                                record.totalLsdScore > 40 ? 'hsl(38 92% 50% / 0.12)': 'hsl(145 60% 40% / 0.12)',
                              color: record.totalLsdScore > 60 ? 'hsl(355 75% 38%)' :
                                record.totalLsdScore > 40 ? 'hsl(38 72% 32%)': 'hsl(145 60% 28%)',
                            }}
                          >
                            {record.totalLsdScore}
                          </span>
                          <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden hidden xl:block">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${Math.min(100, (record.totalLsdScore / 100) * 100)}%`,
                                background: record.totalLsdScore > 60 ? 'hsl(var(--destructive))' :
                                  record.totalLsdScore > 40 ? 'hsl(var(--warning))' : 'hsl(var(--success))',
                              }}
                            />
                          </div>
                        </div>
                      </td>
                      {/* Critical */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {record.criticalFindings > 0 ? (
                            <span className="badge badge-critical tabular-nums">{record.criticalFindings}</span>
                          ) : (
                            <span className="text-xs text-muted">—</span>
                          )}
                          {record.severeFindings > 0 && (
                            <span className="badge badge-severe tabular-nums ml-1">{record.severeFindings}</span>
                          )}
                        </div>
                      </td>
                      {/* PSC Def */}
                      <td className="px-4 py-3">
                        <span className={`font-mono text-xs font-semibold tabular-nums ${record.pscDeficiencies > 10 ? 'text-red-600' : record.pscDeficiencies > 5 ? 'text-amber-600' : 'text-muted'}`}>
                          {record.pscDeficiencies}
                        </span>
                      </td>
                      {/* Status */}
                      <td className="px-4 py-3">
                        <span className={STATUS_CONFIG[record.status]?.class || 'badge'}>
                          {record.status === 'Closed' && <CheckCircle2 size={10} />}
                          {record.status === 'Under Review' && <Clock size={10} />}
                          {record.status === 'Draft' && <FileEdit size={10} />}
                          {STATUS_CONFIG[record.status]?.label}
                        </span>
                      </td>
                      {/* Actions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ opacity: 1 }}>
                          <button
                            title="View inspection details"
                            className="p-1.5 rounded-md hover:bg-muted transition-colors"
                            onClick={() => setDrawerRecord(record)}
                          >
                            <Eye size={14} className="text-muted" />
                          </button>
                          <button
                            title="Edit inspection"
                            className="p-1.5 rounded-md hover:bg-muted transition-colors"
                            onClick={() => toast.info(`Opening ${record.vesselName} for editing`)}
                          >
                            <FileEdit size={14} className="text-muted" />
                          </button>
                          <button
                            title="Delete inspection — this cannot be undone"
                            className="p-1.5 rounded-md hover:bg-red-50 transition-colors"
                            onClick={() => toast.error(`Delete ${record.vesselName}?`, { action: { label: 'Confirm', onClick: () => toast.success('Deleted') } })}
                          >
                            <Trash2 size={14} style={{ color: 'hsl(var(--muted-foreground))' }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: 'hsl(var(--border))' }}>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted">
                Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, filtered.length)} of {filtered.length} inspections
              </span>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted">Per page:</span>
                <select
                  className="text-xs border rounded-md px-1.5 py-1 outline-none"
                  style={{ borderColor: 'hsl(var(--border))' }}
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={`pgsize-${n}`} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                className="p-1.5 rounded-md hover:bg-muted transition-colors disabled:opacity-40"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft size={14} />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={`pg-${pageNum}`}
                    className="w-7 h-7 rounded-md text-xs font-semibold transition-colors"
                    style={{
                      background: page === pageNum ? 'hsl(var(--primary))' : 'transparent',
                      color: page === pageNum ? '#fff' : 'hsl(var(--foreground))',
                    }}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                className="p-1.5 rounded-md hover:bg-muted transition-colors disabled:opacity-40"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-slide-up">
          <div className="flex items-center gap-3 px-5 py-3 rounded-2xl shadow-modal border bg-white"
            style={{ borderColor: 'hsl(var(--border))' }}>
            <span className="text-sm font-semibold">{selectedIds.size} selected</span>
            <div className="w-px h-4 bg-muted" />
            <button className="btn-secondary text-xs" onClick={handleExport}>
              <Download size={13} /> Export Selected
            </button>
            <button className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors hover:bg-red-50"
              style={{ color: 'hsl(var(--destructive))' }}
              onClick={() => { toast.error(`Delete ${selectedIds.size} inspections?`, { action: { label: 'Confirm', onClick: () => { setSelectedIds(new Set()); toast.success(`${selectedIds.size} inspections deleted`); } } }); }}>
              <Trash2 size={13} className="inline mr-1" /> Delete
            </button>
            <button className="text-xs text-muted hover:text-foreground transition-colors" onClick={() => setSelectedIds(new Set())}>
              <X size={13} className="inline" /> Clear
            </button>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      {drawerRecord && (
        <InspectionDetailDrawer record={drawerRecord} onClose={() => setDrawerRecord(null)} />
      )}
    </div>
  );
}